import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  AgentCard,
  ClientAction,
  ReqGuardQualityCard,
  RequirementDiffEntry,
  RequirementDiffResult,
  RequirementSetSummary,
  RunStatisticsResult,
} from '@cypremos/shared'
import { computeWordDiff } from '@/features/auto-test/utils/wordDiff'
import type { Message } from '../ChatPanel'
import type { CoverageRunSummary, GenerationEvidence, RequirementCheckEvidence, ReviewRunSummary } from './chatActionBridgeContext'
import type { ChainStepStatus, ChatBlock, ConfirmGate } from './types'

interface AgentBlocksProps {
  message: Message
  onConfirm: (messageId: string, blockIndex: number) => void
}

function useDate() {
  const { i18n } = useTranslation()
  return (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(i18n.language)
  }
}

const pct = (n: number) => `${Math.round(n * 100)}%`

function StatusDot({ status }: { status: ChainStepStatus | 'running' | 'done' | 'failed' }) {
  const cls = {
    pending: 'bg-fg-muted/40',
    running: 'bg-brand animate-pulse',
    done: 'bg-green-500',
    failed: 'bg-red-500',
    skipped: 'bg-fg-muted/40',
  }[status]
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${cls}`} />
}

function Frame({ children, tone = 'default' }: { children: React.ReactNode; tone?: 'default' | 'warn' | 'brand' }) {
  const border = tone === 'warn' ? 'border-amber-500/40' : tone === 'brand' ? 'border-brand/50' : 'border-line'
  return <div className={`mt-1 rounded-xl border ${border} bg-bg-surface-2/60 px-3 py-2.5 text-sm text-fg`}>{children}</div>
}

/** Supporting information that stays out of the way: collapsed until the person opens it. */
function Collapsed({ summary, tone, children }: { summary: string; tone?: 'default' | 'warn'; children: React.ReactNode }) {
  return (
    <Frame tone={tone}>
      <details>
        <summary className="cursor-pointer text-xs font-medium select-none">{summary}</summary>
        <div className="mt-2 space-y-2">{children}</div>
      </details>
    </Frame>
  )
}

function EvidenceBlock({ evidence }: { evidence: RequirementCheckEvidence }) {
  const { t } = useTranslation()
  // Generation uses only the wording matches; suggestions by meaning are listed but not used.
  const rows = evidence.check.entries
    .map((entry) => {
      const suggested = new Set(entry.knowledgeSuggestions?.map((s) => s.requirementId))
      const ids = entry.knowledge?.requirementIds ?? []
      return {
        entry,
        text: evidence.requirements[entry.index] ?? '',
        used: ids.filter((id) => !suggested.has(id)),
        suggested: ids.filter((id) => suggested.has(id)),
      }
    })
    .filter((row) => row.entry.issues.length > 0 || row.used.length > 0 || row.suggested.length > 0)
  const issueCount = rows.reduce((n, { entry }) => n + entry.issues.length, 0)
  const knowledgeIds = new Set(rows.flatMap((row) => row.used))
  if (rows.length === 0) return null
  const label = (entry: RequirementCheckEvidence['check']['entries'][number], id: string) => {
    const title = entry.knowledge?.details?.find((d) => d.id === id)?.title
    return title ? `${id} ${title}` : id
  }

  return (
    <Collapsed summary={t('chat.agent.evidence.summary', { issues: issueCount, knowledge: knowledgeIds.size })}>
      {rows.map(({ entry, text, used, suggested }) => (
        <div key={entry.index} className="text-xs">
          <div className="font-medium">{t('chat.agent.evidence.requirement', { index: entry.index + 1 })}</div>
          <div className="text-fg-muted line-clamp-2">{text}</div>
          {entry.issues.length > 0 && (
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {entry.issues.map((issue, i) => (
                <li key={i}>
                  <span className="text-amber-500">{t(`autoTest.reqClassify.check.kind.${issue.kind}`)}</span>
                  {' '}{issue.message}
                  <span className="text-fg-muted"> · {t(`autoTest.reqClassify.check.source.${issue.source}`, { items: issue.knowledgeItemIds?.join(', ') ?? '' })}</span>
                </li>
              ))}
            </ul>
          )}
          {used.length > 0 && (
            <div className="mt-1 text-fg-muted">
              {t('chat.agent.evidence.knowledge', { items: used.map((id) => label(entry, id)).join(' / ') })}
            </div>
          )}
          {suggested.length > 0 && (
            <div className="mt-1 text-fg-muted">
              {t('chat.agent.evidence.suggested', { items: suggested.map((id) => label(entry, id)).join(' / ') })}
            </div>
          )}
        </div>
      ))}
      {issueCount > 0 && <div className="text-xs text-fg-muted">{t('chat.agent.evidence.assumptionHelp')}</div>}
      {evidence.check.mode === 'mock' && <div className="text-xs text-fg-muted">{t('chat.agent.evidence.mock')}</div>}
      {rows.some((row) => row.used.length > 0 || row.suggested.length > 0) && <div className="text-xs text-fg-muted">{t('chat.agent.knowledge.draft')}</div>}
    </Collapsed>
  )
}

function GenerationEvidenceBlock({ evidence }: { evidence: GenerationEvidence }) {
  const { t } = useTranslation()
  const cases = evidence.cases.filter((tc) => tc.assumptions.length > 0 || tc.knowledgeItems.length > 0)
  if (cases.length === 0 && !evidence.reference) return null
  const assumptionCount = cases.filter((tc) => tc.assumptions.length > 0).length

  return (
    <Collapsed
      tone={assumptionCount > 0 ? 'warn' : 'default'}
      summary={t('chat.agent.generationEvidence.summary', { assumptions: assumptionCount, knowledge: evidence.reference?.requirementIds.length ?? 0 })}
    >
      {evidence.reference && (
        <div className="text-xs text-fg-muted">
          {t('chat.agent.knowledge.used', { title: evidence.reference.title, version: evidence.reference.version })}
          {evidence.reference.requirementIds.length > 0 && ` · ${t('chat.agent.knowledge.requirements', { ids: evidence.reference.requirementIds.join(', ') })}`}
        </div>
      )}
      {cases.map((tc) => (
        <div key={tc.id} className="text-xs">
          <div className="font-medium">{tc.id} · {tc.name}</div>
          {tc.assumptions.length > 0 && (
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {tc.assumptions.map((assumption, i) => <li key={i}>{assumption}</li>)}
            </ul>
          )}
          {tc.knowledgeItems.length > 0 && (
            <div className="mt-1 text-fg-muted">
              {t('chat.agent.generationEvidence.cites', { items: tc.knowledgeItems.map((item) => `${item.id} ${item.title}`).join(' / ') })}
            </div>
          )}
        </div>
      ))}
      {assumptionCount > 0 && <div className="text-xs text-fg-muted">{t('chat.agent.generationEvidence.assumptionHelp')}</div>}
      {evidence.reference?.status === 'draft' && <div className="text-xs text-fg-muted">{t('chat.agent.knowledge.draft')}</div>}
    </Collapsed>
  )
}

function ActionBlock({ action, status, detail }: { action: ClientAction; status: 'running' | 'done' | 'failed'; detail?: string }) {
  const { t } = useTranslation()
  const source = action.name === 'classify_requirements'
    ? action.source === 'message'
      ? t('chat.agent.action.sourceMessage', { count: action.requirements?.length ?? 0 })
      : t('chat.agent.action.sourceWizard')
    : undefined
  const feedback =
    action.name === 'regenerate_test_case' ? `${action.testCaseId}: ${action.feedback}` :
    action.name === 'add_test_case' ? action.description :
    action.name === 'generate_robot_script' ? action.feedback : undefined

  return (
    <Frame tone={status === 'failed' ? 'warn' : 'default'}>
      <div className="flex items-center gap-2">
        <StatusDot status={status} />
        <span className="font-medium">{t(`chat.agent.action.${action.name}`)}</span>
        <span className="ml-auto text-xs text-fg-muted">{t(`chat.agent.status.${status}`)}</span>
      </div>
      {source && <div className="mt-1 text-xs text-fg-muted">{source}</div>}
      {feedback && <div className="mt-1 text-xs text-fg-muted whitespace-pre-wrap">{t('chat.agent.action.instruction', { text: feedback })}</div>}
      {detail && <div className="mt-1 text-xs">{detail}</div>}
    </Frame>
  )
}

function ConfirmBlock({ gate, status, assumptionCount = 0, onConfirm }: {
  gate: ConfirmGate
  status: 'pending' | 'running' | 'confirmed' | 'expired'
  assumptionCount?: number
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  if (status === 'expired') {
    return <Frame><span className="text-xs text-fg-muted">{t(gate === 'robot' ? 'chat.agent.confirm.robotExpired' : 'chat.agent.confirm.expired')}</span></Frame>
  }
  if (status === 'confirmed') {
    return <Frame><span className="text-xs text-fg-muted">✓ {t('chat.agent.confirm.confirmed')}</span></Frame>
  }
  return (
    <Frame tone="brand">
      <div className="text-xs text-fg-muted mb-2">{t('chat.agent.confirm.humanOnly')}</div>
      {gate === 'robot' && assumptionCount > 0 && (
        <div className="text-xs text-amber-500 mb-2">{t('chat.agent.confirm.assumptions', { count: assumptionCount })}</div>
      )}
      <button
        onClick={onConfirm}
        disabled={status === 'running'}
        className="w-full font-label-md text-label-md bg-brand text-bg-surface px-4 py-2 rounded-full hover:bg-brand/90 transition-colors disabled:opacity-60"
      >
        {t(gate === 'robot' ? 'chat.agent.confirm.robotButton' : 'chat.agent.confirm.button')}
      </button>
    </Frame>
  )
}

function ChainBlock({ block }: { block: Extract<ChatBlock, { kind: 'chain' }> }) {
  const { t } = useTranslation()
  return (
    <Frame tone={block.error ? 'warn' : 'default'}>
      <ol className="flex flex-col gap-1.5">
        {block.steps.map((step) => (
          <li key={step.key} className="flex items-center gap-2">
            <StatusDot status={step.status} />
            <span className={step.status === 'skipped' ? 'text-fg-muted line-through' : ''}>{t(`chat.agent.chain.${step.key}`)}</span>
            <span className="ml-auto text-xs text-fg-muted">{t(`chat.agent.status.${step.status}`)}</span>
          </li>
        ))}
      </ol>
      {block.detail && <div className="mt-2 text-xs">{block.detail}</div>}
      {block.error && <div className="mt-2 text-xs text-red-400">{block.error}</div>}
    </Frame>
  )
}

function InlineWordDiff({ before, after }: { before: string; after: string }) {
  const segments = useMemo(() => computeWordDiff(before, after), [before, after])
  return (
    <div className="whitespace-pre-wrap leading-relaxed text-xs">
      {segments.map((seg, i) =>
        seg.type === 'removed' ? (
          <span key={i} className="bg-red-500/25 text-red-300 line-through rounded-sm px-0.5">{seg.text}</span>
        ) : seg.type === 'added' ? (
          <span key={i} className="bg-green-500/25 text-green-300 rounded-sm px-0.5">{seg.text}</span>
        ) : (
          <span key={i} className="text-fg-muted">{seg.text}</span>
        ),
      )}
    </div>
  )
}

function DiffEntry({ entry }: { entry: Exclude<RequirementDiffEntry, { type: 'unchanged' }> }) {
  const { t } = useTranslation()
  const tag = {
    changed: 'bg-amber-500/20 text-amber-300',
    added: 'bg-green-500/20 text-green-300',
    removed: 'bg-red-500/20 text-red-300',
  }[entry.type]
  return (
    <li className="border-t border-line pt-2">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 ${tag}`}>{t(`chat.agent.card.diff.${entry.type}`)}</span>
        <span className="text-xs font-medium truncate">{entry.key}</span>
      </div>
      {entry.type === 'changed' ? (
        <InlineWordDiff before={entry.before} after={entry.after} />
      ) : (
        <div className={`whitespace-pre-wrap text-xs ${entry.type === 'removed' ? 'line-through text-fg-muted' : ''}`}>{entry.text}</div>
      )}
    </li>
  )
}

function DiffCard({ data }: { data: RequirementDiffResult }) {
  const { t } = useTranslation()
  const date = useDate()
  const differing = data.entries.filter((e): e is Exclude<RequirementDiffEntry, { type: 'unchanged' }> => e.type !== 'unchanged')
  return (
    <Frame>
      <div className="font-medium mb-1">{t('chat.agent.card.diff.title')}</div>
      <div className="text-xs text-fg-muted flex flex-col gap-0.5">
        {data.set && (
          <span>
            {t('chat.agent.card.set', { name: data.set.requirementSetName })}
            {data.set.basis !== 'explicit' && (
              <> · {t(data.set.basis === 'requirement_ids' ? 'chat.agent.card.diff.matchIds' : 'chat.agent.card.diff.matchLines', { pct: pct(data.set.overlap) })}</>
            )}
          </span>
        )}
        {data.base && <span>{t('chat.agent.card.diff.base', { name: data.base.name, date: date(data.base.executedAt) })}</span>}
        <span>
          {data.target
            ? t('chat.agent.card.diff.target', { name: data.target.name, date: date(data.target.executedAt) })
            : t('chat.agent.card.diff.targetCurrent')}
        </span>
      </div>
      <div className="mt-2 text-xs">{t('chat.agent.card.diff.counts', data.counts)}</div>
      {differing.length === 0 ? (
        <div className="mt-2 text-xs text-fg-muted">{t('chat.agent.card.diff.none')}</div>
      ) : (
        <ul className="mt-2 flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
          {differing.map((entry) => <DiffEntry key={`${entry.type}-${entry.key}`} entry={entry} />)}
        </ul>
      )}
      <div className="mt-2 text-[11px] text-fg-muted">{t('chat.agent.card.computedNote')}</div>
    </Frame>
  )
}

function StatsCard({ data }: { data: RunStatisticsResult }) {
  const { t } = useTranslation()
  const date = useDate()
  return (
    <Frame>
      <div className="font-medium mb-1">{t('chat.agent.card.stats.title')}</div>
      {data.set && <div className="text-xs text-fg-muted">{t('chat.agent.card.set', { name: data.set.requirementSetName })}</div>}
      {data.runs.length > 1 && (
        <div className="mt-1 text-xs">{t('chat.agent.card.stats.totals', { ...data.totals, total: data.totals.testCases })}</div>
      )}
      <ul className="mt-2 flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
        {data.runs.map((run) => (
          <li key={run.run.runId} className="border-t border-line pt-2">
            <div className="flex items-center gap-2">
              <StatusDot status={run.failed > 0 ? 'failed' : 'done'} />
              <span className="text-xs font-medium truncate">{run.run.name}</span>
              <span className="ml-auto text-xs text-fg-muted shrink-0">{date(run.run.executedAt)}</span>
            </div>
            <div className="mt-1 text-xs">
              {t('chat.agent.card.stats.runLine', { passed: run.passed, failed: run.failed, total: run.total })}
            </div>
            {run.failures.length > 0 && (
              <ul className="mt-1 flex flex-col gap-1">
                {run.failures.map((f) => (
                  <li key={f.name} className="text-xs rounded-md bg-red-500/10 px-2 py-1">
                    <div className="font-medium text-red-300">{f.name}</div>
                    {f.message && <div className="text-fg-muted break-words">{f.message}</div>}
                  </li>
                ))}
              </ul>
            )}
            {run.failed > 0 && run.failures.length === 0 && (
              <div className="mt-1 text-xs text-fg-muted">{t('chat.agent.card.stats.noDetail')}</div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2 text-[11px] text-fg-muted">{t('chat.agent.card.computedNote')}</div>
    </Frame>
  )
}

function SetsCard({ data }: { data: RequirementSetSummary[] }) {
  const { t } = useTranslation()
  const date = useDate()
  return (
    <Frame>
      <div className="font-medium mb-1">{t('chat.agent.card.sets.title')}</div>
      <ul className="flex flex-col gap-1.5">
        {data.map((set) => (
          <li key={set.requirementSetId} className="flex items-center gap-2 text-xs">
            <StatusDot status={set.latestStatus === 'Failed' ? 'failed' : 'done'} />
            <span className="font-medium truncate">{set.requirementSetName}</span>
            <span className="ml-auto text-fg-muted shrink-0">
              {t('chat.agent.card.sets.row', { count: set.runCount, date: date(set.latestRun.executedAt) })}
            </span>
          </li>
        ))}
      </ul>
    </Frame>
  )
}

function CardBlock({ card }: { card: AgentCard }) {
  if (card.type === 'requirement_diff') return <DiffCard data={card.data} />
  if (card.type === 'run_statistics') return <StatsCard data={card.data} />
  return <SetsCard data={card.data} />
}

// ── Requirement Doctor (Phase 3) ─────────────────────────────

const scoreText = (score: number | null) => (score === null ? '—' : `${score}%`)

/** A coverage round as the page computed it; the model only narrates. */
function CoverageSummaryView({ summary }: { summary: CoverageRunSummary }) {
  const { t } = useTranslation()
  const { overall, byStandard } = summary.scores
  const delta = summary.previousOverall != null && overall !== null ? overall - summary.previousOverall : null
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-fg-muted">{t('chat.reqGuard.coverage.overall')}</span>
        {summary.previousOverall != null && <span className="text-sm text-fg-muted">{scoreText(summary.previousOverall)} →</span>}
        <span className="text-lg font-semibold">{scoreText(overall)}</span>
        {delta !== null && delta !== 0 && (
          <span className={`text-xs font-semibold ${delta > 0 ? 'text-green-500' : 'text-red-500'}`}>{delta > 0 ? `+${delta}` : delta}</span>
        )}
      </div>
      <div className="text-xs text-fg-muted">
        ISO 26262 {scoreText(byStandard.iso26262)} ・ ISO/SAE 21434 {scoreText(byStandard.iso21434)} ・ ISO 21448 {scoreText(byStandard.iso21448)}
      </div>
      <div className="text-xs">
        {t('chat.reqGuard.coverage.counts', { gaps: summary.gapCount, proposals: summary.proposalCount, accepted: summary.acceptedCount })}
      </div>
      <div className="text-[11px] text-fg-muted">{t('chat.reqGuard.coverage.computedNote')}</div>
    </div>
  )
}

/** A finished 要求品質レビュー analysis: the wizard's scores, which are demonstration data for now. */
function ReviewSummaryView({ summary }: { summary: ReviewRunSummary }) {
  const { t } = useTranslation()
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-fg-muted">{t('chat.reqGuard.review.average')}</span>
        <span className="text-lg font-semibold">{summary.averageScore ?? '—'}</span>
        <span className="text-xs text-fg-muted">{t('chat.reqGuard.review.counts', { analyzed: summary.analyzed, issues: summary.issueCount })}</span>
      </div>
      {summary.lowest.length > 0 && (
        <div className="text-xs">
          {t('chat.reqGuard.review.lowest')} {summary.lowest.map(l => `${l.id} ${l.score}`).join(' ・ ')}
        </div>
      )}
      <div className="text-[11px] text-amber-500">{t('chat.reqGuard.review.demoNote')}</div>
    </div>
  )
}

function ReqGuardActionBlock({ block }: { block: Extract<ChatBlock, { kind: 'rgAction' }> }) {
  const { t } = useTranslation()
  const { action, status } = block
  const source = action.name === 'run_coverage'
    ? action.source === 'message'
      ? t('chat.agent.action.sourceMessage', { count: action.spec?.length ?? 0 })
      : t(action.source === 'sample' ? 'chat.reqGuard.action.sourceSample' : 'chat.reqGuard.action.sourcePanel')
    : action.name === 'import_requirements'
      ? action.source === 'message'
        ? t('chat.agent.action.sourceMessage', { count: action.requirements?.length ?? 0 })
        : t('chat.reqGuard.action.sourceReviewSample')
      : undefined
  return (
    <Frame tone={status === 'failed' ? 'warn' : 'default'}>
      <div className="flex items-center gap-2">
        <StatusDot status={status} />
        <span className="font-medium">{t(`chat.agent.action.${action.name}`)}</span>
        <span className="ml-auto text-xs text-fg-muted">{t(`chat.agent.status.${status}`)}</span>
      </div>
      {source && <div className="mt-1 text-xs text-fg-muted">{source}</div>}
      {block.detail && <div className="mt-1 text-xs">{block.detail}</div>}
      {block.summary && <CoverageSummaryView summary={block.summary} />}
      {block.reviewSummary && <ReviewSummaryView summary={block.reviewSummary} />}
      {block.summary && block.summary.proposalCount > 0 && (
        <div className="mt-2 text-xs text-fg-muted">{t('chat.reqGuard.coverage.next')}</div>
      )}
    </Frame>
  )
}

function ReqGuardConfirmBlock({ block, onConfirm }: { block: Extract<ChatBlock, { kind: 'rgConfirm' }>; onConfirm: () => void }) {
  const { t } = useTranslation()
  if (block.status === 'expired') {
    return <Frame><span className="text-xs text-fg-muted">{t('chat.reqGuard.confirm.expired')}</span></Frame>
  }
  if (block.status === 'failed') {
    return <Frame tone="warn"><span className="text-xs">{t('chat.reqGuard.confirm.failed', { error: block.error ?? '' })}</span></Frame>
  }
  if (block.status === 'confirmed') {
    return (
      <Frame>
        <span className="text-xs text-fg-muted">✓ {t('chat.reqGuard.confirm.confirmed')}</span>
        {block.summary && <CoverageSummaryView summary={block.summary} />}
      </Frame>
    )
  }
  return (
    <Frame tone="brand">
      <div className="text-xs text-fg-muted mb-2">{t('chat.reqGuard.confirm.humanOnly')}</div>
      <button
        onClick={onConfirm}
        disabled={block.status === 'running'}
        className="w-full font-label-md text-label-md bg-brand text-bg-surface px-4 py-2 rounded-full hover:bg-brand/90 transition-colors disabled:opacity-60"
      >
        {t('chat.reqGuard.confirm.button', { count: block.acceptedCount })}
      </button>
    </Frame>
  )
}

const QUALITY_PREVIEW = 5

function QualityCard({ data }: { data: ReqGuardQualityCard }) {
  const { t } = useTranslation()
  const row = (item: ReqGuardQualityCard['items'][number], i: number) => (
    <li key={i} className="text-xs">
      <span className="font-medium">{item.segmentId}</span>{' '}
      <span className="text-amber-500">{item.ruleId} {item.ruleName}</span>
      {item.excerpt && <span>「{item.excerpt}」</span>}
      <div className="text-fg-muted">{item.rationale}</div>
    </li>
  )
  return (
    <Frame>
      <div className="flex items-baseline gap-2">
        <span className="font-medium">{t('chat.reqGuard.quality.title')}</span>
        <span className="text-lg font-semibold">{data.score ?? '—'}</span>
        <span className="text-xs text-fg-muted">{t('chat.reqGuard.quality.counts', { requirements: data.requirements, violations: data.violations })}</span>
      </div>
      {data.items.length > 0 && <ul className="mt-2 space-y-1.5">{data.items.slice(0, QUALITY_PREVIEW).map(row)}</ul>}
      {data.items.length > QUALITY_PREVIEW && (
        <details className="mt-1.5">
          <summary className="cursor-pointer text-xs text-fg-muted select-none">{t('chat.reqGuard.quality.more', { count: data.items.length - QUALITY_PREVIEW })}</summary>
          <ul className="mt-1.5 space-y-1.5">{data.items.slice(QUALITY_PREVIEW).map((item, i) => row(item, i + QUALITY_PREVIEW))}</ul>
        </details>
      )}
      <div className="mt-2 text-[11px] text-fg-muted">{t('chat.reqGuard.quality.note', { model: data.modelId })}</div>
    </Frame>
  )
}

/** Renders an agent-chat message's attachments inside the floating Chat. */
export function AgentBlocks({ message, onConfirm }: AgentBlocksProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1 w-full max-w-[95%]">
      {message.blocks?.map((block, index) => {
        switch (block.kind) {
          case 'action':
            return <ActionBlock key={index} action={block.action} status={block.status} detail={block.detail} />
          case 'rejected':
            return (
              <Frame key={index} tone="warn">
                <span className="text-xs">{t(`chat.agent.rejected.${block.reason}`, { name: t(`chat.agent.action.${block.name}`, block.name) })}</span>
              </Frame>
            )
          case 'confirm':
            return <ConfirmBlock key={index} gate={block.gate} status={block.status} assumptionCount={block.assumptionCount} onConfirm={() => onConfirm(message.id, index)} />
          case 'chain':
            return <ChainBlock key={index} block={block} />
          case 'card':
            return <CardBlock key={index} card={block.card} />
          case 'knowledge':
            return (
              <Collapsed
                key={index}
                tone={block.reference.status === 'draft' ? 'warn' : 'default'}
                summary={t('chat.agent.knowledge.used', { title: block.reference.title, version: block.reference.version })}
              >
                {block.reference.requirementIds.length > 0 && (
                  <div className="text-xs text-fg-muted">
                    {t('chat.agent.knowledge.requirements', { ids: block.reference.requirementIds.join(', ') })}
                  </div>
                )}
                {block.reference.status === 'draft' && (
                  <div className="text-xs text-fg-muted">{t('chat.agent.knowledge.draft')}</div>
                )}
              </Collapsed>
            )
          case 'evidence':
            return <EvidenceBlock key={index} evidence={block.evidence} />
          case 'generationEvidence':
            return <GenerationEvidenceBlock key={index} evidence={block.evidence} />
          case 'rgAction':
            return <ReqGuardActionBlock key={index} block={block} />
          case 'rgConfirm':
            return <ReqGuardConfirmBlock key={index} block={block} onConfirm={() => onConfirm(message.id, index)} />
          case 'rgCard':
            return <QualityCard key={index} data={block.card.data} />
        }
      })}
    </div>
  )
}
